import React, { useState, useEffect, useCallback, useRef } from 'react';
import './MerxandatgeApp.css';
import { fetchAPI } from '../../utils/utils';
import MerxandatgeItem from './MerxandatgeItem';
import MerxandatgeForm from './MerxandatgeForm';
import MerxandatgeStockModal from './MerxandatgeStockModal';
import MerxandatgeRoundsModal from './MerxandatgeRoundsModal';
import MerxandatgeOrders from './MerxandatgeOrders';
import MerxandatgeNotifications from './MerxandatgeNotifications';
import MerxandatgeOrderTab from './MerxandatgeOrderTab';
import MerxandatgeStats from './MerxandatgeStats';
import MerxandatgeConfig from './MerxandatgeConfig';
import UserInfo from '../login/UserInfo';
import HeaderTabs from '../interface/HeaderTabs';
import Pressable from '../other/Pressable';
import { can } from '../../utils/permissions';
import { formatMoney } from '../../utils/money';

// Helper to check if the deadline has already passed
const hasDeadlinePassed = (deadlineString) => {
  if (!deadlineString) return false;

  const deadline = new Date(deadlineString);
  if (Number.isNaN(deadline.getTime())) return false;

  return deadline < new Date();
};

function MerxandatgeApp({ socket, userInfo, castellersInfo, ...props }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItemKind, setNewItemKind] = useState('product');
  const [showStockModal, setShowStockModal] = useState(false);
  const [showRoundsModal, setShowRoundsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState('products'); // Default to 'products'
  const [viewMode, setViewMode] = useState(() => {
    // Load from localStorage, or default to 'grid' on mobile, 'list' on desktop
    const saved = localStorage.getItem('merxandatgeViewMode');
    if (saved) return saved;
    // Check if mobile (< 768px)
    const isMobile = window.innerWidth < 768;
    return isMobile ? 'grid' : 'list';
  });
  const [itemImages, setItemImages] = useState({});
  const [itemAllImages, setItemAllImages] = useState({}); // Store all images per item for gallery
  const [showInactiveItems, setShowInactiveItems] = useState(true); // Add state to track visibility preference
  const [orders, setOrders] = useState(() => {
    // Load cached orders from localStorage
    try {
      const cached = localStorage.getItem('merxandatge_orders_cache');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Use cache if less than 5 minutes old
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          return data;
        }
      }
    } catch (e) {
      // Ignore cache errors
    }
    return [];
  });
  const [ordersLoading, setOrdersLoading] = useState(() => {
    // Not loading if we have cached data
    try {
      const cached = localStorage.getItem('merxandatge_orders_cache');
      if (cached) {
        const { timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          return false;
        }
      }
    } catch (e) {
      // Ignore cache errors
    }
    return true;
  });
  const [deletedItems, setDeletedItems] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [imageGallery, setImageGallery] = useState({ show: false, item: null, activeIndex: 0 });
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef(null);

  const isAdmin = can(userInfo, 'MANAGE_MERCHANDISE');
  const canDeleteArchivedItems = can(userInfo, 'DELETE_ARCHIVED_MERCHANDISE');

  // Sorting rules matching MerxandatgeOrderTab
  const sortByRules = (list) => {
    return [...list].sort((a, b) => {
      const aHasOrder = a.display_order !== undefined && a.display_order !== null;
      const bHasOrder = b.display_order !== undefined && b.display_order !== null;

      // If both have display_order, respect it (admin-defined order)
      if (aHasOrder && bHasOrder) {
        return a.display_order - b.display_order;
      }

      // Items with display_order come before those without
      if (aHasOrder && !bHasOrder) return -1;
      if (!aHasOrder && bHasOrder) return 1;

      // Default: deadline items first, then by id
      const aHasDeadline = a.type === 'made_to_order' && a.next_round_deadline;
      const bHasDeadline = b.type === 'made_to_order' && b.next_round_deadline;

      if (aHasDeadline && !bHasDeadline) return -1;
      if (!aHasDeadline && bHasDeadline) return 1;

      if (aHasDeadline && bHasDeadline) {
        return new Date(a.next_round_deadline) - new Date(b.next_round_deadline);
      }

      return b.id - a.id;
    });
  };

  // Get filtered items based on user role and visibility preferences
  const getFilteredItems = () => {
    if (!items) return [];
    
    let filtered = [];
    
    // If user is not admin, only show active items
    if (!isAdmin) {
      filtered = items.filter(item => item.active);
    } else if (isAdmin && showInactiveItems) {
      filtered = items; // Show all items
    } else {
      // If user is admin but doesn't want to see inactive items
      filtered = items.filter(item => item.active);
    }
    
    // Apply the same sorting as the order tab
    return sortByRules(filtered);
  };

  // Parse the current path to determine the active tab
  const getTabFromPath = () => {
    const path = window.location.pathname;
    const parts = path.split('/');
    const lastPart = parts[parts.length - 1];

    if (['products', 'orders', 'notifications', 'ordre', 'estadistiques', 'arxivats', 'configuracio'].includes(lastPart)) {
      return lastPart;
    }
    
    return 'products'; // Default tab
  };

  useEffect(() => {
    if (!showAddMenu) return undefined;
    const handleClickOutside = (event) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        setShowAddMenu(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowAddMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAddMenu]);

  useEffect(() => {
    // Set initial tab based on URL path
    setActiveTab(getTabFromPath());

    // Handle browser back/forward navigation
    const handlePopState = () => {
      setActiveTab(getTabFromPath());
    };

    window.addEventListener('popstate', handlePopState);

    const listeners = [];
    const listen = (event, handler) => {
      listeners.push([event, handler]);
      socket.on(event, handler);
    };
    const requestData = () => {
      socket.emit('.get_all_merxandatge', true);
      if (isAdmin) {
        fetchDeletedItems();
        socket.emit('.get_all_merxandatge_orders');
      }
    };

    // Listen for updates
    listen('.all_merxandatge', (data) => {
      setItems(data);
      setLoading(false);
      
      // Request images for all items
      data.forEach(item => {
        socket.emit('.get_merxandatge_images', item.id);
      });
    });

    listen('.merxandatge_updated', () => {
      socket.emit('.get_all_merxandatge', true);
      if (isAdmin) {
        fetchDeletedItems();
      }
    });
    
    // Handle visibility toggle response
    listen('.merxandatge_visibility_toggled', (response) => {
      if (response.success) {
        // Refresh the items list
        socket.emit('.get_all_merxandatge', true);
      }
    });
    
    // Handle images response
    listen('.merxandatge_images', (data) => {
      if (data.images && data.images.length > 0) {
        setItemImages(prev => ({
          ...prev,
          [data.itemId]: data.images[0].url
        }));
        // Store all images for gallery
        setItemAllImages(prev => ({
          ...prev,
          [data.itemId]: data.images
        }));
      }
    });

    // Load orders for stats tab (only if admin)
    if (isAdmin) {
      listen('.all_merxandatge_orders', (data) => {
        setOrders(data);
        setOrdersLoading(false);
        // Cache orders data
        try {
          localStorage.setItem('merxandatge_orders_cache', JSON.stringify({
            data,
            timestamp: Date.now()
          }));
        } catch (e) {
          // Ignore cache errors
        }
      });

      listen('.deleted_merxandatge', (data) => {
        setDeletedItems(data || []);
        setLoadingDeleted(false);
        
        // Request images for deleted items too
        (data || []).forEach(item => {
          socket.emit('.get_merxandatge_images', item.id);
        });
      });

      listen('.merxandatge_restored', (response) => {
        if (response?.success) {
          socket.emit('.get_all_merxandatge', true);
          fetchDeletedItems();
        } else if (response?.message) {
          alert(response.message);
        }
      });

      listen('.merxandatge_deleted_permanent', (response) => {
        if (response?.success) {
          fetchDeletedItems();
        } else if (response?.message) {
          alert(response.message);
        }
      });
    }

    // Login can reconnect before the initial replies arrive; reload on every connection.
    listen('connect', requestData);
    if (socket.connected) requestData();

    return () => {
      listeners.forEach(([event, handler]) => socket.off(event, handler));
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isAdmin, socket]);

  // Helper function to change tab and update URL path
  const changeTab = (tab) => {
    setActiveTab(tab);
    
    // Get the base URL without the tab part
    const currentPath = window.location.pathname;
    const basePath = currentPath.endsWith('/products') || currentPath.endsWith('/orders') || currentPath.endsWith('/notifications') || currentPath.endsWith('/ordre') || currentPath.endsWith('/estadistiques') || currentPath.endsWith('/arxivats') || currentPath.endsWith('/configuracio')
      ? currentPath.substring(0, currentPath.lastIndexOf('/'))
      : currentPath;
    
    // Ensure we have a trailing slash on the basePath
    const normalizedBasePath = basePath.endsWith('/') ? basePath : basePath + '/';
    
    // Update URL without page reload
    const newPath = `${normalizedBasePath}${tab}`;
    window.history.pushState({ tab }, '', newPath);
  };

  // Helper function to change view mode and save to localStorage
  const changeViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('merxandatgeViewMode', mode);
  };

  const handleAddItem = (kind = 'product') => {
    setNewItemKind(kind);
    setSelectedItem(null);
    setShowAddForm(true);
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setShowAddForm(true);
  };

  const handleManageStock = (item) => {
    setSelectedItem(item);
    if (item?.kind === 'pack') setShowAddForm(true);
    else if (item?.type === 'made_to_order') setShowRoundsModal(true);
    else setShowStockModal(true);
  };

  const handleDeleteItem = (itemId) => {
    if (window.confirm('Segur que vols arxivar aquest article?')) {
      socket.emit('.delete_merxandatge', itemId);
    }
  };

  // Check if item has variants without stock
  const hasVariantsWithoutStock = (item) => {
    if (!item.variants || item.variants.length === 0 || item?.type === 'made_to_order') {
      return false;
    }
    return item.variants.some(v => (v.stock || 0) === 0);
  };

  // Get stock summary for an item (returns JSX for visual display)
  const getStockSummary = (item) => {
    if (item.kind === 'pack') return <span className={`stock-badge ${item.total_stock > 0 ? 'stock-badge-ok' : 'stock-badge-empty'}`}>{item.total_stock} packs · estoc compartit</span>;
    // For made-to-order items, show a distinct badge
    if (item?.type === 'made_to_order') {
      return (
        <span className="stock-badge stock-badge-preorder">
          <i className="fas fa-clock"></i> Sota comanda
        </span>
      );
    }

    if (!item.variants || item.variants.length === 0) {
      return (
        <span className="stock-badge stock-badge-empty">
          <i className="fas fa-times-circle"></i> Sense estoc
        </span>
      );
    }
    
    const totalStock = item.variants.reduce((total, variant) => total + (variant.stock || 0), 0);
    const variantsWithoutStock = item.variants.filter(v => (v.stock || 0) === 0);

    // Helper to get variant label
    const getVariantLabel = (variant) => {
      const parts = [];
      if (variant.size) parts.push(variant.size);
      if (variant.color) parts.push(variant.color);
      return parts.length > 0 ? parts.join(' / ') : 'Base';
    };

    // Determine badge color based on stock status
    let badgeClass = 'stock-badge-ok'; // green - all good
    if (totalStock === 0) {
      badgeClass = 'stock-badge-empty'; // red - no stock
    } else if (variantsWithoutStock.length > 0) {
      badgeClass = 'stock-badge-warning'; // orange - some variants missing
    }

    return (
      <div className="stock-info">
        <span className={`stock-badge ${badgeClass}`}>
          <strong>{totalStock}</strong> unitats
        </span>
        {variantsWithoutStock.length > 0 ? (
          <div className="stock-missing-variants">
            {variantsWithoutStock.map((variant, idx) => (
              <span key={idx} className="stock-missing-label">
                <i className="fas fa-exclamation-triangle"></i> {getVariantLabel(variant)}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const formatDeletionDate = (dateString) => {
    if (!dateString) return '—';

    try {
      const date = new Date(dateString);
      return date.toLocaleString('ca-ES');
    } catch (error) {
      return dateString;
    }
  };

  const getItemImageUrl = (item) => {
    return itemImages[item.id] || item.image_url || '/default-merchandise.png';
  };

  const handleToggleVisibility = (itemId, currentVisibility) => {
    socket.emit('.toggle_merxandatge_visibility', {
      itemId: itemId,
      active: !currentVisibility
    });
  };

  const openImageGallery = (item, startIndex = 0) => {
    setImageGallery({ show: true, item, activeIndex: startIndex });
  };

  const closeImageGallery = () => {
    setImageGallery({ show: false, item: null, activeIndex: 0 });
  };

  const nextGalleryImage = useCallback(() => {
    const images = itemAllImages[imageGallery.item?.id] || [];
    if (images.length <= 1) return;
    setImageGallery(prev => ({
      ...prev,
      activeIndex: prev.activeIndex === images.length - 1 ? 0 : prev.activeIndex + 1
    }));
  }, [imageGallery.item?.id, itemAllImages]);

  const prevGalleryImage = useCallback(() => {
    const images = itemAllImages[imageGallery.item?.id] || [];
    if (images.length <= 1) return;
    setImageGallery(prev => ({
      ...prev,
      activeIndex: prev.activeIndex === 0 ? images.length - 1 : prev.activeIndex - 1
    }));
  }, [imageGallery.item?.id, itemAllImages]);

  // Keyboard navigation for image gallery
  useEffect(() => {
    if (!imageGallery.show) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeImageGallery();
      } else if (e.key === 'ArrowRight') {
        nextGalleryImage();
      } else if (e.key === 'ArrowLeft') {
        prevGalleryImage();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [imageGallery.show, nextGalleryImage, prevGalleryImage]);

  // Add toggle for showing/hiding inactive items (for admins)
  const handleToggleShowInactive = () => {
    setShowInactiveItems(!showInactiveItems);
  };

  const fetchDeletedItems = () => {
    if (!isAdmin) return;
    setLoadingDeleted(true);
    socket.emit('.get_deleted_merxandatge');
  };

  const handleRestoreItem = (itemId) => {
    if (window.confirm('Vols recuperar aquest article?')) {
      socket.emit('.restore_merxandatge', itemId);
    }
  };

  const handlePermanentDelete = (item) => {
    if (!canDeleteArchivedItems) {
      alert('Només els caps de junta poden eliminar articles arxivats.');
      return;
    }

    if (window.confirm(`Segur que vols eliminar "${item.name}" PERMANENTMENT?\n\nAquesta acció no es pot desfer i eliminarà:\n- L'article i les seves variants\n- Totes les imatges\n- Les comandes associades`)) {
      socket.emit('.delete_merxandatge_permanent', item.id);
    }
  };



  const renderListView = () => {
    const filteredItems = getFilteredItems();
    
    return (
      <div className="items-list-view">
        <table className="items-table">
          <thead>
            <tr>
              <th>Imatge</th>
              <th>Nom</th>
              <th>Preu</th>
              <th>Estoc</th>
              {isAdmin && <th>Visibilitat</th>}
              {isAdmin && <th>Accions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredItems.map(item => {
              const deadlinePassed = item.type === 'made_to_order' && hasDeadlinePassed(item.next_round_deadline);
              return (
              <tr key={item.id} className={`item-row ${!item.active ? 'inactive-row' : ''} ${deadlinePassed ? 'deadline-passed-row' : ''}`}>
                <td className="item-image-cell">
                  <div 
                    className="item-thumbnail-wrapper"
                    onClick={() => openImageGallery(item)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img src={getItemImageUrl(item)} alt={item.name} className={`item-thumbnail ${deadlinePassed ? 'deadline-passed-thumbnail' : ''}`} />
                    {(itemAllImages[item.id]?.length || 0) > 1 ? (
                      <span className="image-count-badge">
                        <i className="fas fa-images"></i> {itemAllImages[item.id].length}
                      </span>
                    ) : null}
                  </div>
                  {isAdmin && !item.active ? <span className="inactive-tag">No visible</span> : null}
                </td>
                <td className="item-name-cell">
                  {item.name}{item.kind === 'pack' && <span className="pack-label">Pack</span>}
                  {deadlinePassed && (
                    <span className="deadline-label-list">Data límit passada</span>
                  )}
                </td>
                <td className="item-price-cell">{formatMoney(item.min_price ?? item.price)}€</td>
                <td className="item-stock-cell">{getStockSummary(item)}</td>
                {isAdmin && (
                  <td className="item-visibility-cell">
                    <button 
                      className={`visibility-toggle ${item.active ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleVisibility(item.id, item.active)}
                    >
                      {item.active ? 'Visible' : 'Ocult'}
                    </button>
                  </td>
                )}
                {isAdmin && (
                  <td className="item-actions-cell">
                    <div className="item-actions">
                      <button 
                        onClick={() => handleEditItem(item)} 
                        className="edit-button"
                        style={{
                          backgroundColor: '#3498db',
                          color: 'white',
                          border: 'none',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          minWidth: '100px'
                        }}
                      >
                        Editar
                      </button>
                      <button onClick={() => handleManageStock(item)} className="stock-button" style={{ position: 'relative' }}>
                        {item.kind === 'pack' ? 'Articles del pack' : item?.type === 'made_to_order' ? 'Data límit' : 'Estoc'}
                        {item?.type !== 'made_to_order' && hasVariantsWithoutStock(item) && (
                          <span className="stock-button-badge">!</span>
                        )}
                      </button>
                      <button onClick={() => handleDeleteItem(item.id)} className="delete-button">
                        <i className="fas fa-archive"></i> Arxivar
                      </button>
                    </div>
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTrashView = () => {
    if (loadingDeleted) {
      return (
        <div className="loading">
          <div className="spinner"></div>
          <p>Carregant arxivats...</p>
        </div>
      );
    }

    if (!deletedItems || deletedItems.length === 0) {
      return <div className="no-items trash-empty">No hi ha articles arxivats</div>;
    }

    return (
      <div className="trash-container">
        <div className="trash-helper-text">
          Els articles recuperats tornen a aparèixer a la botiga com a actius.
        </div>
        <table className="items-table trash-table">
          <thead>
            <tr>
              <th>Imatge</th>
              <th>Nom</th>
              <th>Preu</th>
              <th>Unitats venudes</th>
              <th>Arxivat el</th>
              <th>Accions</th>
            </tr>
          </thead>
          <tbody>
            {deletedItems.map(item => (
              <tr key={item.id} className="item-row inactive-row">
                <td className="item-image-cell">
                  <img src={getItemImageUrl(item)} alt={item.name} className="item-thumbnail" />
                </td>
                <td className="item-name-cell">{item.name}</td>
                <td className="item-price-cell">{formatMoney(item.price)}€</td>
                <td className="item-orders-cell">
                  {item.sold_units !== undefined 
                    ? `${item.sold_units} ${item.sold_units === 1 ? 'unitat' : 'unitats'}`
                    : '—'}
                </td>
                <td className="item-date-cell">{formatDeletionDate(item.deleted_at)}</td>
                <td className="item-actions-cell">
                  <div className="trash-actions">
                    <button onClick={() => handleRestoreItem(item.id)} className="restore-button">
                      <i className="fas fa-undo"></i> Recuperar
                    </button>
                    {canDeleteArchivedItems && (
                      <button onClick={() => handlePermanentDelete(item)} className="permanent-delete-button">
                        <i className="fas fa-trash-alt"></i> Eliminar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTabContent = () => {
    if (activeTab === 'orders') {
      return <MerxandatgeOrders socket={socket} userInfo={userInfo} castellersInfo={castellersInfo} />;
    }
    
    if (activeTab === 'notifications') {
      return <MerxandatgeNotifications socket={socket} userInfo={userInfo} />;
    }

    if (activeTab === 'ordre') {
      return <MerxandatgeOrderTab
        items={items}
        socket={socket}
        isAdmin={isAdmin}
        itemImages={itemImages}
      />;
    }

    if (activeTab === 'estadistiques') {
      const deletedItemIds = new Set((deletedItems || []).map(item => item.id));
      return <MerxandatgeStats socket={socket} orders={orders} itemImages={itemImages} deletedItemIds={deletedItemIds} items={items} deletedItems={deletedItems} loading={ordersLoading} />;
    }

    if (activeTab === 'arxivats') {
      return renderTrashView();
    }

    if (activeTab === 'configuracio') {
      return <MerxandatgeConfig socket={socket} />;
    }

    const filteredItems = getFilteredItems();

    return (
      <>
        <div className="merxandatge-controls">
          {isAdmin && (
            <div className="merxandatge-add-split" ref={addMenuRef}>
              <button className="merxandatge-add-article-btn merxandatge-add-split-main" onClick={() => handleAddItem('product')}>
                <i className="fas fa-plus-circle"></i> Afegir nou article
              </button>
              <button
                className="merxandatge-add-article-btn merxandatge-add-split-toggle"
                onClick={() => setShowAddMenu(open => !open)}
                aria-haspopup="menu"
                aria-expanded={showAddMenu}
                title="Més opcions"
              >
                <i className={`fas fa-chevron-${showAddMenu ? 'up' : 'down'}`}></i>
              </button>
              {showAddMenu ? (
                <div className="merxandatge-add-menu" role="menu">
                  <button
                    className="merxandatge-add-menu-item"
                    role="menuitem"
                    onClick={() => {
                      setShowAddMenu(false);
                      handleAddItem('pack');
                    }}
                  >
                    <i className="fas fa-box-open"></i> Afegir nou pack
                  </button>
                </div>
              ) : null}
            </div>
          )}
          
          <div className="view-toggle">
            <button 
              className={`view-button ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => changeViewMode('grid')}
              title="Veure en graella"
            >
              <i className="fas fa-th"></i> Graella
            </button>
            <button 
              className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => changeViewMode('list')}
              title="Veure en llista"
            >
              <i className="fas fa-list"></i> Llista
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Carregant productes...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="no-items">No hi ha articles disponibles actualment</div>
        ) : viewMode === 'list' ? (
          renderListView()
        ) : (
          <div className="items-grid">
            {filteredItems.map(item => (
              <MerxandatgeItem 
                key={item.id}
                item={item}
                isAdmin={isAdmin}
                onEdit={() => handleEditItem(item)}
                onDelete={() => handleDeleteItem(item.id)}
                onManageStock={() => handleManageStock(item)}
                onToggleVisibility={() => handleToggleVisibility(item.id, item.active)}
                hasVariantsWithoutStock={hasVariantsWithoutStock(item)}
                socket={socket}
              />
            ))}
          </div>
        )}

        {showAddForm && (
          <MerxandatgeForm 
            catalog={items}
            initialKind={newItemKind}
            item={selectedItem}
            onClose={handleCloseForm}
            isEdit={!!selectedItem}
            socket={socket}
          />
        )}

        {showStockModal && (
          <MerxandatgeStockModal
            item={selectedItem}
            onClose={() => setShowStockModal(false)}
            socket={socket}
          />
        )}

        {showRoundsModal && (
          <MerxandatgeRoundsModal
            item={selectedItem}
            onClose={() => setShowRoundsModal(false)}
            socket={socket}
          />
        )}

        {imageGallery.show && imageGallery.item && (
          <div className="image-gallery-overlay" onClick={closeImageGallery}>
            <div className="image-gallery-modal" onClick={(e) => e.stopPropagation()}>
              <button className="gallery-close-button" onClick={closeImageGallery}>
                <i className="fas fa-times"></i>
              </button>
              
              <div className="gallery-header">
                <h3>{imageGallery.item.name}</h3>
              </div>
              
              <div className="gallery-content">
                {(itemAllImages[imageGallery.item.id]?.length || 0) > 1 ? (
                  <button className="gallery-nav-button gallery-prev" onClick={prevGalleryImage}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                ) : null}
                
                <div className="gallery-image-container">
                  <img 
                    src={itemAllImages[imageGallery.item.id]?.[imageGallery.activeIndex]?.url || getItemImageUrl(imageGallery.item)}
                    alt={imageGallery.item.name}
                    className="gallery-main-image"
                  />
                </div>
                
                {(itemAllImages[imageGallery.item.id]?.length || 0) > 1 ? (
                  <button className="gallery-nav-button gallery-next" onClick={nextGalleryImage}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                ) : null}
              </div>
              
              {(itemAllImages[imageGallery.item.id]?.length || 0) > 1 ? (
                <div className="gallery-thumbnails">
                  {itemAllImages[imageGallery.item.id].map((img, index) => (
                    <img
                      key={index}
                      src={img.url}
                      alt={`${imageGallery.item.name} - ${index + 1}`}
                      className={`gallery-thumbnail ${index === imageGallery.activeIndex ? 'active' : ''}`}
                      onClick={() => setImageGallery(prev => ({ ...prev, activeIndex: index }))}
                    />
                  ))}
                </div>
              ) : null}
              
              {(itemAllImages[imageGallery.item.id]?.length || 0) > 1 ? (
                <div className="gallery-counter">
                  {imageGallery.activeIndex + 1} / {itemAllImages[imageGallery.item.id].length}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="merxandatge-container">
      <UserInfo castellersInfo={castellersInfo} {...props} socket={socket} userInfo={userInfo} />
      <HeaderTabs castellersInfo={castellersInfo} userInfo={userInfo} {...props} />

      <Pressable style={{ backgroundColor: '#eee' }} className="boto-back" href='/'>
          ← Tornar a la pàgina principal
      </Pressable>

      <h1>Botiga</h1>

      <div className="page-layout">
        <div className="navigation-tabs">
          <button
            className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => changeTab('products')}
          >
            <i className="fas fa-shopping-bag"></i> Productes
          </button>
          {isAdmin && (
            <>
              <button
                className={`tab-button ${activeTab === 'ordre' ? 'active' : ''}`}
                onClick={() => changeTab('ordre')}
              >
                <i className="fas fa-sort"></i> Ordre
              </button>
              <button
                className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
                onClick={() => changeTab('orders')}
              >
                <i className="fas fa-clipboard-list"></i> Comandes
              </button>
              <button
                className={`tab-button ${activeTab === 'estadistiques' ? 'active' : ''}`}
                onClick={() => changeTab('estadistiques')}
              >
                <i className="fas fa-chart-bar"></i> Estadístiques
              </button>
              <button
                className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
                onClick={() => changeTab('notifications')}
              >
                <i className="fas fa-bell"></i> Notificacions
              </button>
              <button
                className={`tab-button ${activeTab === 'configuracio' ? 'active' : ''}`}
                onClick={() => changeTab('configuracio')}
              >
                <i className="fas fa-cog"></i> Configuració
              </button>
              <button
                className={`tab-button ${activeTab === 'arxivats' ? 'active' : ''}`}
                onClick={() => changeTab('arxivats')}
              >
                <i className="fas fa-archive"></i> Arxivats
              </button>
            </>
          )}
        </div>

        <div className="page-content">
          {renderTabContent()}
        </div>
      </div>

      <div style={{ height: '100px' }}></div>
    </div>
  );
}

export default MerxandatgeApp;
